import { useStore } from '@/infra/store';
import { logger } from '@/infra/logger/Logger';
import { getCloudClient } from '@/infra/cloud/CloudClient';
import { saveCodec } from '@/infra/persistence/save-codec';
import { CURRENT_SCHEMA_MINOR } from '@/infra/persistence/schema-v6';
import type { SaveV6 } from '@/infra/persistence/schema-v6';
import { buildSnapshot } from './save';
import { eventBus } from '@/infra/event-bus/EventBus';

const AUTO_SYNC_INTERVAL_MS = 60_000;
const QUOTA_WARN_BYTES = 500_000;

let intervalId: ReturnType<typeof setInterval> | null = null;
let unsubAuth: (() => void) | null = null;

function jsonSize(obj: unknown): number {
  return JSON.stringify(obj).length;
}

async function pushNow(): Promise<{ ok: boolean; error?: string }> {
  const client = getCloudClient();
  if (!client.isAvailable()) return { ok: false, error: 'cloud-disabled' };
  const s = useStore.getState();
  if (!s.settings.cloud.enabled) return { ok: false, error: 'cloud-toggle-off' };
  if (!s.cloudUser) return { ok: false, error: 'not-authenticated' };

  s.setCloudSyncStatus('syncing');
  const snap = buildSnapshot();
  const size = jsonSize(snap);
  if (size > QUOTA_WARN_BYTES) {
    logger.warn('cloud.sync.large-save', { bytes: size });
  }
  const r = await client.upsertSave(s.cloudUser.id, {
    payload: snap,
    updatedAt: snap.meta.savedAt,
    schemaMinor: snap.schemaMinor,
  });
  if (r.ok) {
    s.setCloudSyncStatus('synced');
    s.setCloudLastSyncAt(Date.now());
    s.setCloudLastError(null);
    eventBus.emit('cloud.synced', {});
    logger.info('cloud.sync.pushed');
  } else {
    s.setCloudSyncStatus('error');
    s.setCloudLastError(r.error ?? 'unknown');
    logger.warn('cloud.sync.push-failed', { err: r.error });
  }
  return r;
}

async function pullNow(): Promise<{
  ok: boolean;
  applied?: boolean;
  error?: string;
}> {
  const client = getCloudClient();
  if (!client.isAvailable()) return { ok: false, error: 'cloud-disabled' };
  const s = useStore.getState();
  if (!s.cloudUser) return { ok: false, error: 'not-authenticated' };

  const cloudSave = await client.fetchSave(s.cloudUser.id);
  if (!cloudSave) return { ok: true, applied: false };

  // Validate decode
  const decoded = saveCodec.decode(cloudSave.payload);
  if (!decoded) {
    return { ok: false, error: 'cloud-corrupted' };
  }

  // Conflict resolution: cloud vence se updated_at > local.savedAt
  const localSavedAt = useStore.getState().creature
    ? buildSnapshot().meta.savedAt
    : 0;

  if (cloudSave.updatedAt > localSavedAt) {
    applySnapshotToStore(decoded);
    s.setCloudLastSyncAt(Date.now());
    s.setCloudSyncStatus('synced');
    logger.info('cloud.sync.pulled-applied', { cloudAt: cloudSave.updatedAt, localAt: localSavedAt });
    eventBus.emit('cloud.applied-from-cloud', {});
    return { ok: true, applied: true };
  }
  logger.info('cloud.sync.local-newer', { cloudAt: cloudSave.updatedAt, localAt: localSavedAt });
  return { ok: true, applied: false };
}

function applySnapshotToStore(snap: SaveV6): void {
  const s = useStore.getState();
  if (snap.creature) {
    const c = snap.creature;
    s.setActiveCreature({
      ...c,
      maxHp: c.maxHp ?? undefined,
      mutations: c.mutations ?? undefined,
    } as never);
  }
  if (snap.npcs) s.setNpcs(snap.npcs as never);
  if (snap.lineage) s.setLineage(snap.lineage as never);
  if (snap.discoveredTypes) s.setDiscoveredTypes(snap.discoveredTypes);
  if (snap.world) s.setWorld(snap.world as never);
}

async function startAutoSync(): Promise<void> {
  if (intervalId !== null) return;
  intervalId = setInterval(() => {
    void pushNow();
  }, AUTO_SYNC_INTERVAL_MS);
  // Initial pull
  await pullNow();
}

function stopAutoSync(): void {
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
}

async function signInWithEmail(
  email: string,
): Promise<{ ok: boolean; error?: string }> {
  const client = getCloudClient();
  if (!client.isAvailable()) return { ok: false, error: 'cloud-disabled' };
  return client.signInWithEmail(email, window.location.href);
}

async function signOut(): Promise<void> {
  const client = getCloudClient();
  await client.signOut();
  stopAutoSync();
  useStore.getState().setCloudUser(null);
  useStore.getState().setCloudSyncStatus('idle');
}

function start(): void {
  const client = getCloudClient();
  const s = useStore.getState();

  if (!client.isAvailable()) {
    s.setCloudSyncStatus('disabled');
    return;
  }

  // Auth state listener
  unsubAuth = client.onAuthStateChange((user) => {
    useStore.getState().setCloudUser(user);
    if (user && useStore.getState().settings.cloud.enabled) {
      void startAutoSync();
    } else {
      stopAutoSync();
    }
  });

  // Bootstrap: check for existing session
  void client.getUser().then((user) => {
    s.setCloudUser(user);
    if (user && useStore.getState().settings.cloud.enabled) {
      void startAutoSync();
    }
  });

  // Watch settings.cloud.enabled changes
  let lastEnabled = s.settings.cloud.enabled;
  useStore.subscribe((state) => {
    if (state.settings.cloud.enabled !== lastEnabled) {
      lastEnabled = state.settings.cloud.enabled;
      if (lastEnabled && state.cloudUser) {
        void startAutoSync();
      } else {
        stopAutoSync();
      }
    }
  });
}

function stop(): void {
  stopAutoSync();
  unsubAuth?.();
  unsubAuth = null;
}

export interface CloudSyncApi {
  start: () => void;
  stop: () => void;
  pushNow: () => Promise<{ ok: boolean; error?: string }>;
  pullNow: () => Promise<{ ok: boolean; applied?: boolean; error?: string }>;
  signInWithEmail: (email: string) => Promise<{ ok: boolean; error?: string }>;
  signOut: () => Promise<void>;
  isAvailable: () => boolean;
}

let api: CloudSyncApi | null = null;

export function getCloudSyncService(): CloudSyncApi {
  if (api) return api;
  api = {
    start,
    stop,
    pushNow,
    pullNow,
    signInWithEmail,
    signOut,
    isAvailable: () => getCloudClient().isAvailable(),
  };
  return api;
}

export function resetCloudSyncForTests(): void {
  stop();
  api = null;
}

// Use SCHEMA_MINOR variable to avoid lint dead-code
void CURRENT_SCHEMA_MINOR;
